import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Navbar.css';

interface NavbarProps {
  onBooking: () => void;
}

export default function Navbar({ onBooking }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const navLinks = [
    { label: 'Servicios', href: '#servicios' },
    { label: 'Nosotros', href: '#nosotros' },
    { label: 'El Barbero', href: '#barbero' },
    { label: 'Galería', href: '#galeria' },
    { label: 'Contacto', href: '#contacto' },
  ];

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      const navHeight = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 32;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`navbar fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
          scrolled ? 'navbar-scrolled' : 'navbar-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-14 h-14 flex items-center justify-center">
              <img
                src="https://storage.readdy-site.link/project_files/0d0d1fd6-befb-4f18-9f8b-efee19c55c96/fe9d8cd5-f344-4aec-86a2-924460c8dc34_LOGO-sin-letra.png?v=e571b3f4ddc76f76526cd8e946a6178b"
                alt="Barber Studio Luisfer Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="navbar-logo-text text-white font-semibold text-base tracking-widest uppercase hidden sm:block">
              Barber Studio <span className="footer-logo-accent">Luisfer</span>
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
                className="navbar-link cursor-pointer"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center">
            <Link to="/reservar" className="navbar-cta cursor-pointer">
              Reservar Cita
            </Link>
          </div>

          {/* Hamburger — with background so it's always visible */}
          <button
            className="navbar-hamburger md:hidden cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menú"
            aria-expanded={menuOpen}
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl`} />
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      <div
        className={`navbar-mobile-overlay fixed inset-0 z-40 flex flex-col md:hidden transition-all duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Transparent top zone — clicking it closes the menu */}
        <div className="h-20 flex-shrink-0" onClick={() => setMenuOpen(false)} />

        {/* Menu content panel */}
        <div className="navbar-mobile-panel flex-1 flex flex-col items-center justify-center gap-8 px-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
              className="navbar-mobile-link cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <Link
            to="/reservar"
            onClick={() => setMenuOpen(false)}
            className="navbar-mobile-cta cursor-pointer"
          >
            Reservar Cita
          </Link>
        </div>
      </div>
    </>
  );
}
